import { Chrome, Check, ArrowUpRight, Sparkles } from "lucide-react";
import ExtensionStatsStrip from "@/components/landing/ExtensionStatsStrip";

// Live Chrome Web Store listing.
const CHROME_STORE_URL = "https://chromewebstore.google.com/detail/shescan-brand-finder/kiiinbbipichfgnnbncfpcfgaipcecom";
// Live SheScan web app (mobile-friendly, no download required).
const WEB_APP_URL = "https://app.shescan.app";

/* -------- Browser mock -------- */

function BrowserMock() {
  return (
    <div className="relative">
      {/* Browser frame */}
      <div className="border border-line bg-white">
        {/* Top chrome bar */}
        <div className="flex items-center gap-3 px-4 py-3 border-b border-line bg-white">
          <div className="flex gap-1.5">
            <span className="w-2.5 h-2.5 rounded-full border border-line bg-white" />
            <span className="w-2.5 h-2.5 rounded-full border border-line bg-white" />
            <span className="w-2.5 h-2.5 rounded-full border border-line bg-white" />
          </div>
          <div className="flex-1 mx-3 border border-line bg-white px-3 py-1.5 font-mono text-[10px] sm:text-[11px] text-ink-soft truncate">
            <span className="text-brand-orange">●</span>{" "}
            https://shop.example.com/eco-skincare-set
          </div>
          <div className="hidden sm:flex items-center gap-2 border border-line bg-white px-2.5 py-1.5">
            <Chrome className="w-3.5 h-3.5 text-brand-orange" />
            <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-ink">
              SheScan
            </span>
          </div>
        </div>

        {/* Page body — left: faux product, right: extension popup */}
        <div className="relative grid grid-cols-12 min-h-[300px] sm:min-h-[360px]">
          {/* Faux product card */}
          <div className="col-span-12 sm:col-span-8 p-6 sm:p-8 border-r border-line">
            <div className="flex flex-col gap-4">
              <div className="aspect-[16/9] sm:aspect-[3/2] border border-line bg-white flex items-center justify-center">
                <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-ink-soft">
                  Eco Skincare Set · Photo
                </span>
              </div>
              <div className="space-y-2">
                <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-ink-soft">
                  Legacy brand · not women-owned
                </span>
                <h4 className="font-display font-bold uppercase text-ink text-xl leading-tight">
                  Eco Skincare Starter Set
                </h4>
                <div className="flex items-center gap-3">
                  <span className="font-mono text-lg text-ink">$48.00</span>
                  <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-ink-soft">
                    Free shipping
                  </span>
                </div>
              </div>
              <button className="self-start border border-line bg-ink text-white font-mono uppercase text-[11px] tracking-[0.22em] px-4 py-2.5">
                Add to cart
              </button>
            </div>
          </div>

          {/* Sidebar popup */}
          <aside
            data-testid="extension-popup"
            className="absolute sm:static right-3 top-3 sm:right-auto sm:top-auto sm:col-span-4 w-[60%] sm:w-auto border border-brand-orange bg-white sm:m-5 sm:self-start"
          >
            <div className="flex items-center justify-between px-3 py-2 border-b border-line bg-brand-orange text-ink">
              <span className="font-mono text-[9px] sm:text-[10px] uppercase tracking-[0.22em] flex items-center gap-1.5">
                <Sparkles className="w-3 h-3" />
                SheScan
              </span>
              <span className="font-mono text-[9px] uppercase tracking-[0.18em] text-ink-soft">
                Alert
              </span>
            </div>
            <div className="p-3 sm:p-4 space-y-3">
              <div className="flex items-center gap-2 text-brand-orange">
                <Check className="w-4 h-4" />
                <span className="font-mono text-[10px] uppercase tracking-[0.22em]">
                  Verified alternative
                </span>
              </div>
              <p className="font-display font-bold uppercase text-ink text-sm sm:text-base leading-tight">
                Women-owned alternative found.
              </p>
              <div className="border-t border-line pt-3 space-y-1">
                <div className="font-display font-bold text-ink text-sm">
                  Sister Skincare Co.
                </div>
                <div className="font-mono text-[10px] text-ink-soft">
                  100% women-owned · USA
                </div>
                <div className="font-mono text-sm text-ink mt-1">$44.00</div>
              </div>
              <button className="w-full inline-flex items-center justify-center gap-2 border border-line bg-white text-ink border border-line font-mono uppercase text-[10px] tracking-[0.22em] px-3 py-2 hover:bg-brand-orange hover:border-brand-orange transition-colors">
                Swap & save
                <ArrowUpRight className="w-3 h-3" />
              </button>
            </div>
          </aside>
        </div>
      </div>

      {/* Sticker label */}
      <span className="absolute -top-3 left-6 inline-flex items-center gap-2 bg-white text-ink border border-line font-mono text-[10px] uppercase tracking-[0.22em] px-3 py-1.5 rotate-[-2deg]">
        <Sparkles className="w-3 h-3 text-brand-orange" />
        Browser pop-up demo
      </span>
    </div>
  );
}

export default function ChromeExtension() {
  const track = (item_id) => {
    try {
      window.gtag?.("event", "select_content", {
        content_type: "cta",
        item_id,
      });
    } catch { /* noop */ }
  };

  return (
    <section
      id="extension"
      data-testid="extension-section"
      className="relative border-b border-line bg-white"
    >
      <div className="w-full max-w-none px-4 sm:px-8 lg:px-12 py-20 sm:py-28 lg:py-32">
        <div className="flex items-center justify-between mb-14">
          <span className="font-mono text-[11px] uppercase tracking-[0.28em] text-ink-soft">
            04 / Chrome Extension
          </span>
          <span className="hidden sm:inline font-mono text-[11px] uppercase tracking-[0.28em] text-link">
            Desktop utility · Live
          </span>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start">
          <div className="lg:col-span-5">
            <h2
              data-testid="extension-headline"
              className="font-display font-bold tracking-tight text-ink text-[1.75rem] sm:text-[2rem] lg:text-[2.25rem] leading-[1.2]"
            >
              Bring SheScan to your{" "}
              <span className="text-brand-orange">browser</span>.
            </h2>

            <div className="mt-8 space-y-5 text-lg text-ink-soft leading-relaxed max-w-xl">
              <p>
                The Chrome extension watches the product page or service
                platform you&apos;re already on — and quietly surfaces a
                verified women-owned alternative whenever the brand in your
                cart or provider on your screen isn&apos;t.
              </p>
              <p className="text-ink">
                One install. Every purchase and contract, audited at checkout.
              </p>
            </div>

            <div className="mt-10 flex flex-col sm:flex-row gap-4">
              <a
                href={CHROME_STORE_URL}
                target="_blank"
                rel="noreferrer"
                onClick={() => track("extension_install_chrome")}
                data-testid="extension-cta-primary"
                className="group inline-flex items-center justify-center gap-3 border-2 border-brand-orange bg-brand-orange text-white font-sans font-bold uppercase text-sm tracking-[0.22em] px-8 py-5 hover:bg-white hover:border-line hover:text-ink transition-colors duration-150"
              >
                <Chrome className="w-5 h-5" />
                Install Chrome Extension
              </a>
              <a
                href={WEB_APP_URL}
                target="_blank"
                rel="noreferrer"
                onClick={() => track("extension_open_web_app")}
                data-testid="extension-cta-secondary"
                className="inline-flex items-center justify-center gap-3 border-2 border-line bg-white text-ink font-sans font-bold uppercase text-sm tracking-[0.22em] px-8 py-5 hover:border-link transition-colors duration-150"
              >
                Open the app
                <ArrowUpRight className="w-4 h-4" />
              </a>
            </div>

            <ExtensionStatsStrip />

            <ul className="mt-10 border-t border-line space-y-0">
              {[
                "Auto-detects e-commerce items and professional service listings on supported sites",
                "Verified ownership data, not superficial branding signals",
                "One-click swap to a verified women-owned alternative",
                "Works seamlessly alongside the mobile web app — powered by the same live database",
              ].map((line, i) => (
                <li
                  key={i}
                  className="py-3 border-b border-line flex items-start gap-3 text-sm text-ink"
                >
                  <Check className="w-4 h-4 mt-0.5 text-brand-orange shrink-0" />
                  <span>{line}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-7">
            <BrowserMock />
          </div>
        </div>
      </div>
    </section>
  );
}
